
import React, { 
  createContext, 
  useContext, 
  useState, 
  useCallback, 
  useEffect, 
  useMemo, 
  ReactNode 
} from 'react';
import {
  SoloChoreographerState,
  SoloChoreographerContextType,
  SoloConfig,
  SoloPhrase,
  SoloLength,
  SoloInstrument,
  SoloOutput
} from '@/types/soloChoreographer';
import { useSoloTemplates } from '@hooks/useSoloTemplates';
import { generateSoloOutput, injectSoloIntoLyrics } from './utils/soloOutputGenerator';

// ═══════════════════════════════════════════════════════════════
// DEFAULT STATE
// ═══════════════════════════════════════════════════════════════

function generateId() {
  return crypto.randomUUID?.() || Math.random().toString(36).substring(2, 11);
}

function createDefaultPhrases(length: SoloLength): SoloPhrase[] {
  const phraseCount = length / 2;
  const roles: Array<'entrance' | 'develop' | 'build' | 'peak' | 'resolve' | 'exit'> = 
    ['entrance', 'develop', 'build', 'peak', 'resolve', 'exit'];
  
  const phrases: SoloPhrase[] = [];
  
  for (let i = 0; i < phraseCount; i++) {
    const startBar = i * 2 + 1;
    const endBar = startBar + 1;
    const roleIndex = Math.min(i, roles.length - 1);
    
    // Adjust role based on position
    let role = roles[roleIndex];
    if (phraseCount <= 4) {
      // For 8 bars: entrance, develop, build, peak
      const shortRoles: typeof roles = ['entrance', 'develop', 'build', 'peak'];
      role = shortRoles[i] || 'peak';
    }
    
    phrases.push({
      id: generateId(),
      barRange: `${startBar}-${endBar}`,
      name: `Phrase ${i + 1}`,
      role,
      techniqueId: null,
      register: 'mid',
      intensity: 50,
      vibrato: 'none',
      effects: 'none',
      wah: 'off',
      bandInstruction: 'full_groove',
      customDescription: ''
    });
  }
  
  return phrases;
}

const defaultConfig: SoloConfig = {
  enabled: false,
  length: 8,
  instrument: 'lead_guitar',
  phrases: createDefaultPhrases(8)
};

const defaultState: SoloChoreographerState = {
  config: defaultConfig,
  selectedPhraseIndex: 0,
  activeTemplateId: null
};

// ═══════════════════════════════════════════════════════════════
// CONTEXT
// ═══════════════════════════════════════════════════════════════

const SoloChoreographerContext = createContext<SoloChoreographerContextType | null>(null);

// ═══════════════════════════════════════════════════════════════
// PROVIDER
// ═══════════════════════════════════════════════════════════════

// Fix: explicitly allow optional children to fix JSX mapping issue in some TypeScript environments
interface SoloChoreographerProviderProps {
  children?: ReactNode;
}

export function SoloChoreographerProvider({ children }: SoloChoreographerProviderProps) {
  const { data: soloTemplates } = useSoloTemplates();
  // State
  const [config, setConfig] = useState<SoloConfig>(defaultConfig);
  const [selectedPhraseIndex, setSelectedPhraseIndex] = useState(0);
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);

  // Load from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('hookhouse_solo_choreographer');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.config) {
          setConfig(parsed.config);
        }
        if (parsed.activeTemplateId) {
          setActiveTemplateId(parsed.activeTemplateId);
        }
      }
    } catch (e) {
      console.warn('Failed to load Solo-Choreographer state:', e);
    }
  }, []);

  // Save to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('hookhouse_solo_choreographer', JSON.stringify({
        config,
        activeTemplateId
      }));
    } catch (e) {
      console.warn('Failed to save Solo-Choreographer state:', e);
    }
  }, [config, activeTemplateId]);

  // ─────────────────────────────────────────────────────────────
  // Reactive Output
  // ─────────────────────────────────────────────────────────────

  const output = useMemo<SoloOutput | null>(() => {
    if (!config.enabled) return null;
    return generateSoloOutput(config);
  }, [config]);

  // ─────────────────────────────────────────────────────────────
  // Config Actions
  // ─────────────────────────────────────────────────────────────

  const setEnabled = useCallback((enabled: boolean) => {
    setConfig(prev => ({ ...prev, enabled }));
  }, []);

  const setLength = useCallback((length: SoloLength) => {
    setConfig(prev => {
      // Rebuild phrases for new length
      const newPhrases = createDefaultPhrases(length);
      
      // Try to preserve existing phrase content where possible
      const minPhrases = Math.min(prev.phrases.length, newPhrases.length);
      for (let i = 0; i < minPhrases; i++) {
        newPhrases[i] = {
          ...newPhrases[i],
          name: prev.phrases[i].name,
          techniqueId: prev.phrases[i].techniqueId,
          register: prev.phrases[i].register,
          intensity: prev.phrases[i].intensity,
          vibrato: prev.phrases[i].vibrato,
          effects: prev.phrases[i].effects,
          wah: prev.phrases[i].wah,
          bandInstruction: prev.phrases[i].bandInstruction,
          customDescription: prev.phrases[i].customDescription
        };
      }
      
      return { ...prev, length, phrases: newPhrases };
    });
    setActiveTemplateId(null); // Custom now
    setSelectedPhraseIndex(0);
  }, []);

  const setInstrument = useCallback((instrument: SoloInstrument) => {
    setConfig(prev => ({ ...prev, instrument }));
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Phrase Actions
  // ─────────────────────────────────────────────────────────────

  const selectPhrase = useCallback((index: number) => {
    setSelectedPhraseIndex(index);
  }, []);

  const updatePhrase = useCallback((index: number, updates: Partial<SoloPhrase>) => {
    setConfig(prev => {
      const newPhrases = [...prev.phrases];
      if (newPhrases[index]) {
        newPhrases[index] = { ...newPhrases[index], ...updates };
      }
      return { ...prev, phrases: newPhrases };
    });
    setActiveTemplateId(null); // Now custom
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Template Actions
  // ─────────────────────────────────────────────────────────────

  const applyTemplate = useCallback((templateId: string) => {
    const template = soloTemplates.find(t => t.id === templateId);
    if (!template) return;

    // Convert template phrases to full phrases with IDs
    const phrases: SoloPhrase[] = template.phrases.map((tp, i) => ({
      ...tp,
      id: generateId(),
      barRange: tp.barRange || `${i * 2 + 1}-${i * 2 + 2}`
    }));

    setConfig(prev => ({
      ...prev,
      enabled: true,
      length: template.length,
      instrument: template.defaultInstrument,
      phrases
    }));
    
    setActiveTemplateId(templateId);
    setSelectedPhraseIndex(0);
  }, [soloTemplates]);

  const clearToCustom = useCallback(() => {
    setConfig(prev => ({
      ...prev,
      phrases: createDefaultPhrases(prev.length)
    }));
    setActiveTemplateId('custom');
    setSelectedPhraseIndex(0);
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Output Helpers
  // ─────────────────────────────────────────────────────────────

  const getSoloBlock = useCallback((): string => {
    return output?.layerBlock || '';
  }, [output]);

  const getSectionHeader = useCallback((): string => {
    return output?.sectionHeader || '';
  }, [output]);

  // ─────────────────────────────────────────────────────────────
  // Lyrics Integration
  // ─────────────────────────────────────────────────────────────

  const applySoloToLyrics = useCallback((lyrics: string): string => {
    if (!output || !output.layerBlock) return lyrics;
    return injectSoloIntoLyrics(lyrics, output);
  }, [output]);

  // ─────────────────────────────────────────────────────────────
  // Reset
  // ─────────────────────────────────────────────────────────────

  const clearSolo = useCallback(() => {
    setConfig(defaultConfig);
    setActiveTemplateId(null);
    setSelectedPhraseIndex(0);
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Context Value
  // ─────────────────────────────────────────────────────────────

  const state: SoloChoreographerState = {
    config,
    selectedPhraseIndex,
    activeTemplateId
  };

  const contextValue: SoloChoreographerContextType = {
    state,
    output,
    setEnabled,
    setLength,
    setInstrument,
    selectPhrase,
    updatePhrase,
    applyTemplate,
    clearToCustom,
    getSoloBlock,
    getSectionHeader,
    applySoloToLyrics,
    clearSolo
  };

  return (
    <SoloChoreographerContext.Provider value={contextValue}>
      {children}
    </SoloChoreographerContext.Provider>
  );
}

// ═══════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════

export function useSoloChoreographer(): SoloChoreographerContextType {
  const context = useContext(SoloChoreographerContext);
  if (!context) {
    throw new Error('useSoloChoreographer must be used within SoloChoreographerProvider');
  }
  return context;
}